import { Component, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef,
  ChangeDetectorRef, OnDestroy, AfterViewChecked, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { IKeyValuePair } from '../../services/rest/base.service';
import { StringHelper } from '../../utils/string-helper';

@Component({
  selector: 'app-time-combo-box',
  templateUrl: './time-combo-box.component.html',
  styleUrls: ['./time-combo-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeComboBoxComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {

  constructor(private cdr: ChangeDetectorRef) { }

  get items() {
    return this.entries.map(e => ({ key: e[this.keyName], value: e[this.valueName] }));
  }

  @Output() selectionChanged = new EventEmitter<{ key: number | string, value: string }>();

  @Input() keyName = 'key';
  @Input() valueName = 'value';

  @Input() entries: IKeyValuePair<number | string, string>[] = [];
  @Input() preSelectedKey: number | string;
  @Input() preSelectedValue: string;
  @Input() firstEntryAsFallback: boolean;

  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() autofocus: boolean;
  /**
   * the date on which the time is referrin to
   */
  @Input() targetDate: Date = new Date();

  @ViewChild('wrapper') wrapper: ElementRef;
  @ViewChild('inputField') inputField: ElementRef;

  shouldExpand = false;

  selectedValue = '';
  selectedKey: number;

  /**
 * callback functions called in the ngAfterViewChecked hook
 */
  afterViewCheckedTasks: Function[] = [];

  maskPatternFull: RegExp = /^([0-1]?\d|2[0-3])?[\.\:][0-5]\d?$/;
  typingMaskPattern: RegExp = /^(2[0-3]|[0-1]?\d)?([\.:][0-5]\d?|[\.:])?$/;

  validationModel = {
    maskPatternParts: [/\d{1,2}/, /\d{1,2}/],
    maskDelimiterConfig: [[':', 2]],
  };

  maskPatternParts: [RegExp, RegExp] = [/^(2[0-3]|[0-1]?\d)/, /^[0-5]\d?$/];
  maskDelimiterConfig: [[string, number]] = [[':', 2]];
  lastValidTimeValue = '';

  ngOnInit() {
    // * workaround handler for closing checkbox dropdown
    document.addEventListener('click', this.globalClickHandler.bind(this));
  }

  ngOnChanges() {
    // abort if evaluation is obsolete
    if (this.selectedKey === this.preSelectedKey ||
      this.selectedValue === this.preSelectedValue) { return; }

    let selected;
    if (this.preSelectedKey) {
      selected = this.items.find(i => this.preSelectedKey === i.key) ||
        this.generateSelectionFromKey(this.preSelectedKey);

    } else if (this.preSelectedValue) {
      selected = this.items.find(i => this.preSelectedValue === i.value) ||
        this.generateSelectionFromValue(this.preSelectedValue,);

    } else if (this.firstEntryAsFallback && this.items.length > 0) {
      // ? broadcast fallback selection, since input selection was not found as an option?
      selected = this.items[0];
    }

    // if selection matches dropdown entry
    if (selected) {
      this.selectedKey = selected.key;
      this.selectedValue = selected.value;
    }
  }

  // lifecycle callback hook after view value updated with model value
  ngAfterViewChecked() {
    let f;
    // * process hand crafted scheduler task queue
    while (f = this.afterViewCheckedTasks.shift()) { f(); }
  }

  ngOnDestroy() {
    this.cdr.detach();
    // * workaround handler for closing checkbox dropdown
    document.removeEventListener('click', this.globalClickHandler.bind(this));
  }

  applyMask(input) {
    const maskPatternParts = this.maskPatternParts;
    const maskDelimiterConfig = this.maskDelimiterConfig;

    const delimiters = new RegExp(`[${maskDelimiterConfig.map(([delimiter]) => delimiter).join('')}]`, 'g');
    const normalizedInput: string = input.trim().replace(delimiters, '');

    const transformedInput = maskDelimiterConfig
      .filter(([, position]) => normalizedInput.length > position)
      .sort(([, leftPosition], [, rightPosition]) => rightPosition - leftPosition)
      .reduce((transformingInput, [delimiter, position]) => this.stringInsertAt(transformingInput, position, delimiter)
        , normalizedInput);

    const allPartsValid = transformedInput.split(delimiters).every((p, i) => maskPatternParts[i].test(p));

    return allPartsValid ? transformedInput : null;
  }

  stringSplice(string, index, count, add) {
    if (index >= string.length) { return string; }

    const charArray = string.split('');
    charArray.splice(index, count, add);
    return charArray.join('');
  }

  stringInsertAt(string, index, insertString) {
    return this.stringSplice(string, index, 0, insertString);
  }

  generateSelectionFromKey(key: number | string) {
    const numKey = Number(key);
    const value = DateHelper.createDisplayTime(numKey);

    return { key, value };
  }

  /**
   * generates a KeyValue selection model from the displayTime value passed
   * @param value display time to extract hours and minutes from
   * @param keyAsString the key is a number by default, set true to convert to string
   * @returns {IKeyValuePair}
   */
  generateSelectionFromValue(value: string, keyAsString: boolean = false) {
    const numKey = DateHelper.convertDisplayTimeToMinsPastMidnight(value);
    const key = keyAsString === true ? `${numKey}` : numKey;

    return { key, value };
  }

  // click on a drop down item
  onSelect(key: number, value: string) {
    this.selectedKey = key;
    this.selectedValue = value;
    this.closeDropdown();
    this.selectionChanged.emit({ key, value });
  }

  toggleDropdown() {
    // avoid opening empty dropdown item list
    if (this.items.length === 0 && this.shouldExpand !== true) { return; }

    this.shouldExpand = !this.shouldExpand;

    if (this.shouldExpand) {
      this.inputField.nativeElement.select();
    }
  }

  closeDropdown() {
    this.shouldExpand = false;
  }

  globalClickHandler(ev: MouseEvent) {
    const clickWithinScope = this.wrapper.nativeElement.contains(ev.target);
    if (clickWithinScope || this.shouldExpand !== true) { return; }

    this.closeDropdown();
    this.cdr.markForCheck();
  }

  onLeaveField(blurEvent: Event, eventTarget: HTMLInputElement) {
    const inputContent = eventTarget.value;
    // abort if no content in input
    if (!inputContent) { return; }

    if (this.getMinutesSinceMidnight(inputContent) === Number(this.selectedKey)) { return; }

    const selection = this.completeInput(inputContent);
    const { key: minSinceMidnight, value: displayTime } = selection;
    // abort also when no changes were made
    if (Number(this.selectedKey) === Number(minSinceMidnight)) { return; }

    this.selectedValue = displayTime;
    this.selectedKey = minSinceMidnight;
    this.selectionChanged.emit(selection);
  }

  getHoursMinutesToken(inputContent: string): [number, number] {
    // normalize seperator
    inputContent = inputContent.replace('.', ':');
    inputContent = isFinite(Number(inputContent)) ? `${inputContent}:` : inputContent;
    inputContent = inputContent.startsWith(':') ? `00${inputContent}` : inputContent;
    inputContent = inputContent.endsWith(':') ? `${inputContent}00` : inputContent;

    const [hours, minutes] = inputContent.split(':', 2).map((s, i) => {
      // * check length of minute section to ensure 09 isn't interperated as 90 (09 * 10)
      return i === 1 && s.length === 1 && Number(s || 0) < 10 ? Number(s || 0) * 10 : Number(s || 0);
    });

    return [hours, minutes];
  }

  getMinutesSinceMidnight(inputContent: string): number {
    const [hrs, mins] = this.getHoursMinutesToken(inputContent);
    return (hrs * 60) + mins;
  }

  completeInput(inputContent: string) {
    const [hours, minutes] = this.getHoursMinutesToken(inputContent);
    const roundedMinutes = 5 * Math.round(minutes / 5); // round minutes to 5 exact

    let minSinceMidnight = (hours * 60) + roundedMinutes;
    const cmod = DateHelper.getCurrentMinuteOfDay();

    // round to the next 5 minute step if current time is between rounding gap and is today's date
    if (roundedMinutes < minutes && cmod - minSinceMidnight < 5 &&
      DateHelper.AreSameDate(this.targetDate, new Date())) {
      minSinceMidnight += 5;
    }

    const displayTime = DateHelper.createDisplayTime(minSinceMidnight);

    return { key: minSinceMidnight, value: displayTime, inputMinutesSinceMidnight: hours * 60 + minutes };
  }

  patternMatchInput(inputEvent: KeyboardEvent, eventTarget: HTMLInputElement) {

    const inputContent = eventTarget.value;
    // * compensate for when first highlighted char is a seperator for correct curser placement
    const highlightStart = eventTarget.selectionEnd !== eventTarget.selectionStart
      && /^[\.\:]/.test(inputContent[eventTarget.selectionStart])
      ? eventTarget.selectionStart + 1
      : eventTarget.selectionStart;

    const highlightEnd = eventTarget.selectionEnd;
    const inputValue = StringHelper.replaceSection(inputContent, inputEvent.key, highlightStart, highlightEnd);

    // ! Workaround:
    // * reason: keypress is not triggered for unprintable chars, so last char removed with back key and ..
    // * re-typed does not trigger a change in value because the new input value matched old property value
    // : purpose: this ensures input field is updated with last character to match existing property value
    if (this.selectedValue !== inputValue) {
      inputEvent.preventDefault();
    }

    const v = this.typingMaskPattern.test(inputValue);

    if (v === true) {
      this.selectedValue = inputValue;

    } else if (isFinite(Number(inputValue))) { // check if string input is a valid number
      // then insert the seperator (:) if it was left out
      const av = this.applyMask(inputValue);

      if (av && this.typingMaskPattern.test(av)) {
        this.selectedValue = av;
      }
    }

    // * indicates text was selected and handles input correctly for overwriting text
    if (highlightStart !== highlightEnd) {
      const cursorPosition = highlightStart + inputEvent.key.length;

      // ! HACK: task scheduler is manually crafted.
      // TODO: Use Angular time scheduler instead (ngZone) if possible
      // schedule task for correct mouse placement after overwriting highlighted text
      this.afterViewCheckedTasks.push(function (eventTargetRef: HTMLInputElement, cursorPositionRef: number) {
        eventTargetRef.setSelectionRange(cursorPosition, cursorPositionRef);
      }.bind(this, eventTarget, cursorPosition));
    }
  }

}
